// components/Dashboard/tabs/LibraryTab.js
import React, { useState, useMemo, useEffect, useRef } from 'react';
import Masonry from 'react-masonry-css';
import './LibraryTab.css';
import { toast } from 'react-hot-toast';
import QuickContentModal from '../../Modals/QuickContentModal/QuickContentModal';
import QuickButtonUpdateModal from '../../Modals/QuickButtonUpdateModal/QuickButtonUpdateModal';
import BulkVideoWatermarkModal from '../../Modals/BulkVideoWatermarkModal/BulkVideoWatermarkModal';
import InstagramImportModal from '../../Modals/InstagramImportModal/InstagramImportModal';
import ContentModal from '../../Modals/ContentModal/ContentModal';
import InstagramProfileModal from '../../Modals/InstagramProfileModal/InstagramProfileModal';

const LibraryTab = ({
  contentLibrary,
  contentPagination,
  onPageChange,
  onAddContent,
  onEditContent,
  onDeleteContent,
  onShowHistory,
  onSearch,
  tags,
  API_URL,
  onRefreshContent,
  selectedTagForLibrary,
  clearFilterTag,
  contentTagStats
}) => {
  const [searchTag, setSearchTag] = useState('');
  const [expandedCards, setExpandedCards] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState('updated_at_desc');
  const itemsPerPage = 20;
  const [isUpdatingStats, setIsUpdatingStats] = useState(false);
  const [showQuickContentModal, setShowQuickContentModal] = useState(false);
  const [updatingContentIds, setUpdatingContentIds] = useState(new Set());
  const [showQuickButtonModal, setShowQuickButtonModal] = useState(false);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedContents, setSelectedContents] = useState([]);
  const [showBulkWatermarkModal, setShowBulkWatermarkModal] = useState(false);
  const [showInstagramImportModal, setShowInstagramImportModal] = useState(false);
  const [showInstagramProfileModal, setShowInstagramProfileModal] = useState(false);

  // Bulk Edit
  const [showBulkEditModal, setShowBulkEditModal] = useState(false);
  const [bulkEditForm, setBulkEditForm] = useState({
    title: '',
    content: '',
    description: ''
  });
  const [bulkButtonForm, setBulkButtonForm] = useState([]);

  // Yeni state'ler - Etiket düzenleme için
  const [editingTagsForContent, setEditingTagsForContent] = useState(null);
  const [showAddTagDropdown, setShowAddTagDropdown] = useState(null);

  // Yeni etiket oluşturma için
  const [creatingTagForContent, setCreatingTagForContent] = useState(null);
  const [newTagName, setNewTagName] = useState('');
  const [isCreatingTag, setIsCreatingTag] = useState(false);

  // Arama dropdown için yeni state ve ref
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const searchContainerRef = useRef(null);

  // Masonry için breakpoint ayarları
  const breakpointColumns = {
    default: 5,
    1400: 2,
    768: 1
  };

  // En çok kullanılan 5 etiketi hesapla
  const topUsedTags = useMemo(() => {
    if (contentTagStats && contentTagStats.length > 0) {
      return contentTagStats
        .filter(tag => tag.usage_count > 0)
        .slice(0, 5)
        .map(tag => ({
          id: tag.id,
          name: tag.name,
          color: tag.color,
          usageCount: tag.usage_count
        }));
    }
    return [];
  }, [contentTagStats]);

  useEffect(() => {
    if (selectedTagForLibrary) {
      // Tag objesini bul
      const tag = tags.find(t => t.id === selectedTagForLibrary);
      if (tag) {
        setSearchTag(tag.name);
        setCurrentPage(1);
      }
    }
  }, [selectedTagForLibrary, tags]);

  // Click outside handler for search dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Arama dropdown'u
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        setShowSearchDropdown(false);
      }

      // Etiket dropdown'u
      if (showAddTagDropdown && !event.target.closest('.add-tag-inline-list') && !event.target.closest('.add-tag-btn')) {
        setShowAddTagDropdown(null);
      }

      // Yeni etiket input'u - dışarı tıklandığında kapat
      if (creatingTagForContent && !event.target.closest('.new-tag-input-wrapper') && !event.target.closest('.create-tag-btn')) {
        setCreatingTagForContent(null);
        setNewTagName('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showAddTagDropdown, creatingTagForContent]);

  const toggleExpand = (contentId) => {
    setExpandedCards(prev => ({
      ...prev,
      [contentId]: !prev[contentId]
    }));
  };

  // Etiket düzenleme modunu aç/kapa
  const toggleTagEditMode = (contentId, event) => {
    event.stopPropagation();
    if (editingTagsForContent === contentId) {
      setEditingTagsForContent(null);
      setShowAddTagDropdown(null);
      setCreatingTagForContent(null);
      setNewTagName('');
    } else {
      setEditingTagsForContent(contentId);
      setShowAddTagDropdown(null);
      setCreatingTagForContent(null);
      setNewTagName('');
    }
  };

  // İçerikten etiket kaldır
  const handleRemoveTag = async (contentId, tagId, event) => {
    event.stopPropagation();

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/content-library/${contentId}/tags/${tagId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (data.success) {
        toast.success('✅ Etiket kaldırıldı');

        // Sayfa yenilemek yerine içerikleri yeniden yükle
        if (onRefreshContent) {
          await onRefreshContent();
        }
      } else {
        toast.error('❌ Etiket kaldırılamadı: ' + (data.message || 'Bilinmeyen hata'));
      }
    } catch (error) {
      console.error('Etiket kaldırma hatası:', error);
      toast.error('❌ Bağlantı hatası oluştu');
    }
  };

  // İçeriğe yeni etiket ekle
  const handleAddTagToContent = async (contentId, tagId, event) => {
    event.stopPropagation();

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/content-library/${contentId}/tags`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ tagId })
      });

      const data = await response.json();

      if (data.success) {
        toast.success('✅ Etiket eklendi');
        setShowAddTagDropdown(null);

        // Sayfa yenilemek yerine içerikleri yeniden yükle
        if (onRefreshContent) {
          await onRefreshContent();
        }
      } else {
        toast.error('❌ Etiket eklenemedi: ' + (data.message || 'Bilinmeyen hata'));
      }
    } catch (error) {
      console.error('Etiket ekleme hatası:', error);
      toast.error('❌ Bağlantı hatası oluştu');
    }
  };

  // Yeni etiket oluştur ve içeriğe ekle
  const handleCreateAndAddTag = async (contentId, event) => {
    if (event) event.preventDefault();

    const tagName = newTagName.trim();
    if (!tagName || isCreatingTag) return;

    setIsCreatingTag(true);

    try {
      const token = localStorage.getItem('token');

      // Önce yeni etiketi oluştur
      const createResponse = await fetch(`${API_URL}/tags`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: tagName,
          color: '#' + Math.floor(Math.random() * 16777215).toString(16).padStart(6, '0') // Rastgele renk
        })
      });

      const createData = await createResponse.json();

      if (createData.success && createData.tag) {
        // Sonra etiketi içeriğe ekle
        const addResponse = await fetch(`${API_URL}/content-library/${contentId}/tags`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ tagId: createData.tag.id })
        });

        const addData = await addResponse.json();

        if (addData.success) {
          toast.success(`✅ "${tagName}" etiketi oluşturuldu ve eklendi`);
          setNewTagName('');
          setCreatingTagForContent(null);

          if (onRefreshContent) {
            await onRefreshContent();
          }
        } else {
          toast.error('❌ Etiket içeriğe eklenemedi: ' + (addData.message || 'Bilinmeyen hata'));
        }
      } else {
        toast.error('❌ Etiket oluşturulamadı: ' + (createData.message || 'Bilinmeyen hata'));
      }
    } catch (error) {
      console.error('Etiket oluşturma hatası:', error);
      toast.error('❌ Bağlantı hatası oluştu');
    } finally {
      setIsCreatingTag(false);
    }
  };

  // ESC tuşuna basıldığında kapat
  const handleNewTagKeyDown = (e, contentId) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleCreateAndAddTag(contentId);
    } else if (e.key === 'Escape') {
      setCreatingTagForContent(null);
      setNewTagName('');
    }
  };

  // Dropdown'u toggle et
  const toggleAddTagDropdown = (contentId, event) => {
    event.stopPropagation();
    setShowAddTagDropdown(showAddTagDropdown === contentId ? null : contentId);
  };

  const handleUpdateStatistics = async () => {
    if (isUpdatingStats) return;

    setIsUpdatingStats(true);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/statistics/update-all`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (data.success) {
        toast.success('İstatistik güncelleme başlatıldı! İşlem arka planda devam ediyor.');
      } else {
        toast.error('❌ İstatistik güncelleme başlatılamadı: ' + (data.message || 'Bilinmeyen hata'));
      }
    } catch (error) {
      console.error('İstatistik güncelleme hatası:', error);
      toast.error('❌ Bağlantı hatası oluştu');
    } finally {
      setIsUpdatingStats(false);
    }
  };

  // Tek içerik istatistiğini güncelle
  const handleUpdateContentStats = async (contentId, event) => {
    event.stopPropagation();

    if (updatingContentIds.has(contentId)) return;

    setUpdatingContentIds(prev => new Set(prev).add(contentId));

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/content-library/${contentId}/statistics/update`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (data.success) {
        toast.success(`✅ ${data.data.updated} gönderi güncellendi`);

        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } else {
        toast.error('❌ İstatistikler güncellenemedi: ' + (data.message || 'Bilinmeyen hata'));
      }
    } catch (error) {
      console.error('İçerik istatistik güncelleme hatası:', error);
      toast.error('❌ Bağlantı hatası oluştu');
    } finally {
      setUpdatingContentIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(contentId);
        return newSet;
      });
    }
  };

  const handleQuickContentSubmit = (count) => {
    if (window.location.reload) {
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    }
  };

  const handleSearch = (value) => {
    setSearchTag(value);
    setCurrentPage(1);
    if (onSearch) onSearch(value, 1, sortBy);
  };

  // Etiket seçildiğinde
  const handleTagSelect = (tagName) => {
    setSearchTag(tagName);
    setCurrentPage(1);
    setShowSearchDropdown(false);
    if (onSearch) {
      onSearch(tagName, 1, sortBy);
    }
  };

  const parseMediaData = (content) => {
    let mediaItems = [];

    try {
      if (content.media_urls && Array.isArray(content.media_urls)) {
        mediaItems = content.media_urls.map((url, index) => ({
          url: url,
          type: content.media_types ? content.media_types[index] : 'image'
        }));
      } else if (content.media_path) {
        if (content.media_path.startsWith('[')) {
          const paths = JSON.parse(content.media_path);
          const types = content.media_type ? JSON.parse(content.media_type) : [];
          mediaItems = paths.map((path, index) => ({
            url: `/uploads/${path}`,
            type: types[index] || 'image'
          }));
        } else {
          mediaItems = [{
            url: `/uploads/${content.media_path}`,
            type: content.media_type || 'image'
          }];
        }
      }
    } catch (e) {
      console.error('Media parse error:', e);
      if (content.media_path) {
        mediaItems = [{
          url: `/uploads/${content.media_path}`,
          type: content.media_type || 'image'
        }];
      }
    }

    return mediaItems;
  };

  const handleClearSearch = () => {
    setSearchTag('');
    setCurrentPage(1);
    if (onSearch) {
      onSearch('', 1, sortBy);
    }
    if (clearFilterTag) {
      clearFilterTag();
    }
  };

  // Çoklu seçim fonksiyonları
  const toggleSelectionMode = () => {
    setSelectionMode(!selectionMode);
    setSelectedContents([]);
  };

  const toggleContentSelection = (contentId) => {
    setSelectedContents(prev => {
      if (prev.includes(contentId)) {
        return prev.filter(id => id !== contentId);
      } else {
        return [...prev, contentId];
      }
    });
  };

  const selectAllVideos = () => {
    const videoContents = filteredContent.filter(content => {
      const mediaItems = parseMediaData(content);
      return mediaItems.some(item => item.type === 'video');
    });
    setSelectedContents(videoContents.map(c => c.id));
  };

  const clearSelection = () => {
    setSelectedContents([]);
  };

  // Seçilen içeriklerin video olanlarını al
  const getSelectedVideoContents = () => {
    return contentLibrary.filter(content => {
      if (!selectedContents.includes(content.id)) return false;
      const mediaItems = parseMediaData(content);
      return mediaItems.some(item => item.type === 'video');
    });
  };

  const selectedVideoCount = getSelectedVideoContents().length;

  // Sıralama fonksiyonu
  const sortContent = (content, sortType) => {
    let sortedContent = [...content];

    if (sortType === 'buttons_with') {
      sortedContent = sortedContent.filter(item => {
        if (!item.buttons) return false;
        if (item.buttons === null) return false;
        if (item.buttons === 'null') return false;
        if (item.buttons === '[]') return false;
        if (item.buttons === '') return false;

        try {
          const buttons = typeof item.buttons === 'string' ? JSON.parse(item.buttons) : item.buttons;
          if (!Array.isArray(buttons)) return false;
          if (buttons.length === 0) return false;

          const hasValidButton = buttons.some(row => {
            if (Array.isArray(row)) {
              return row.some(btn => btn && btn.text && btn.url);
            }
            return row && row.text && row.url;
          });

          return hasValidButton;
        } catch (e) {
          return false;
        }
      });
      return sortedContent.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));

    } else if (sortType === 'buttons_without') {
      sortedContent = sortedContent.filter(item => {
        if (!item.buttons) return true;
        if (item.buttons === null) return true;
        if (item.buttons === 'null') return true;
        if (item.buttons === '[]') return true;
        if (item.buttons === '') return true;

        try {
          const buttons = typeof item.buttons === 'string' ? JSON.parse(item.buttons) : item.buttons;
          if (!Array.isArray(buttons)) return true;
          if (buttons.length === 0) return true;

          const hasValidButton = buttons.some(row => {
            if (Array.isArray(row)) {
              return row.some(btn => btn && btn.text && btn.url);
            }
            return row && row.text && row.url;
          });

          return !hasValidButton;
        } catch (e) {
          return true;
        }
      });
      return sortedContent.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
    }

    switch (sortType) {
      case 'usage_count_desc':
        return sortedContent.sort((a, b) => (b.usage_count || 0) - (a.usage_count || 0));
      case 'usage_count_asc':
        return sortedContent.sort((a, b) => (a.usage_count || 0) - (b.usage_count || 0));
      case 'views_desc':
        return sortedContent.sort((a, b) => {
          const bViews = b.total_stats?.views || b.view_count || b.views || 0;
          const aViews = a.total_stats?.views || a.view_count || a.views || 0;
          return bViews - aViews;
        });
      case 'views_asc':
        return sortedContent.sort((a, b) => {
          const aViews = a.total_stats?.views || a.view_count || a.views || 0;
          const bViews = b.total_stats?.views || b.view_count || b.views || 0;
          return aViews - bViews;
        });
      case 'created_at_desc':
        return sortedContent.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
      case 'created_at_asc':
        return sortedContent.sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0));
      case 'updated_at_desc':
        return sortedContent.sort((a, b) => new Date(b.updated_at || b.created_at || 0) - new Date(a.updated_at || a.created_at || 0));
      case 'updated_at_asc':
        return sortedContent.sort((a, b) => new Date(a.updated_at || a.created_at || 0) - new Date(b.updated_at || b.created_at || 0));
      case 'title_asc':
        return sortedContent.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
      case 'title_desc':
        return sortedContent.sort((a, b) => (b.title || '').localeCompare(a.title || ''));
      default:
        return sortedContent;
    }
  };

  // Filtrelenmiş ve sıralanmış içerikler
  const filteredContent = useMemo(() => {
    return sortContent(contentLibrary, sortBy);
  }, [contentLibrary, sortBy]);

  const handleSortChange = (newSort) => {
    setSortBy(newSort);
    setCurrentPage(1);
    if (onSearch) onSearch(searchTag, 1, newSort);
  };

  // Sayfalandırma hesaplamaları
  const totalPages = contentPagination?.totalPages || 1;
  const total = contentPagination?.total || contentLibrary.length;
  const startIndex = (currentPage - 1) * 20;
  const endIndex = Math.min(startIndex + 20, total);
  const currentContent = filteredContent; // zaten 20 kayıt geliyor


const goToPage = (page) => {
    setCurrentPage(page);
    if (onPageChange) onPageChange(page, sortBy, searchTag);
    window.scrollTo({ top: 0, behavior: 'smooth' });
};

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      goToPage(currentPage - 1);
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      goToPage(currentPage + 1);
    }
  };

  const getPageNumbers = () => {
    const delta = 2;
    const range = [];
    const rangeWithDots = [];
    let l;

    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || (i >= currentPage - delta && i <= currentPage + delta)) {
        range.push(i);
      }
    }

    range.forEach((i) => {
      if (l) {
        if (i - l === 2) {
          rangeWithDots.push(l + 1);
        } else if (i - l !== 1) {
          rangeWithDots.push('...');
        }
      }
      rangeWithDots.push(i);
      l = i;
    });

    return rangeWithDots;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Bilinmiyor';

    let date;

    if (dateString.includes('T')) {
      if (!dateString.endsWith('Z')) {
        date = new Date(dateString + 'Z');
      } else {
        date = new Date(dateString);
      }
    } else if (dateString.includes(' ')) {
      const isoString = dateString.replace(' ', 'T') + 'Z';
      date = new Date(isoString);
    } else {
      date = new Date(dateString);
    }

    if (isNaN(date.getTime())) {
      console.error('Geçersiz tarih formatı:', dateString);
      return 'Bilinmiyor';
    }

    const now = new Date();
    const diffTime = now - date;
    const diffMinutes = Math.floor(diffTime / (1000 * 60));
    const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffTime < 0) {
      return 'Gelecek tarih';
    }

    if (diffMinutes < 5) {
      return 'Az önce';
    }

    if (diffMinutes < 60) {
      return `${diffMinutes} dakika önce`;
    }

    if (diffHours < 24) {
      const turkeyDateStr = date.toLocaleDateString('tr-TR', { timeZone: 'Europe/Istanbul' });
      const turkeyNowStr = now.toLocaleDateString('tr-TR', { timeZone: 'Europe/Istanbul' });

      if (turkeyDateStr === turkeyNowStr) {
        return diffHours === 1 ? '1 saat önce' : `${diffHours} saat önce`;
      }
    }

    if (diffDays === 1) {
      return 'Dün ' + date.toLocaleTimeString('tr-TR', {
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'Europe/Istanbul'
      });
    }

    if (diffDays > 1 && diffDays < 7) {
      return `${diffDays} gün önce`;
    }

    return date.toLocaleString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Europe/Istanbul'
    });
  };

  const formatStatsUpdateTime = (content) => {
    if (content.stats_updated_at) {
      return formatDate(content.stats_updated_at);
    }
    return 'Hiç güncellenmedi';
  };

  const handleBulkEdit = () => {
    setBulkEditForm({
      title: '',
      content: '',
      description: ''
    });
    setBulkButtonForm([]);
    setShowBulkEditModal(true);
  };

  // Toplu silme
  const handleBulkDelete = async () => {
    if (selectedContents.length === 0) return;

    const confirmText = `${selectedContents.length} icerik kalici olarak silinecek. Bu islem geri alinamaz.\n\nDevam etmek istiyor musunuz?`;
    if (!window.confirm(confirmText)) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/content-library/bulk-delete`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ contentIds: selectedContents })
      });

      const data = await response.json();

      if (data.success) {
        toast.success(`${data.data?.deleted || selectedContents.length} icerik silindi`);
        setSelectionMode(false);
        setSelectedContents([]);
        if (onRefreshContent) await onRefreshContent();
      } else {
        toast.error('Toplu silme hatasi: ' + (data.message || 'Bilinmeyen hata'));
      }
    } catch (error) {
      console.error('Bulk delete hatasi:', error);
      toast.error('Baglanti hatasi olustu');
    }
  };

  const handleBulkSubmit = async (contentIds, updates) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/content-library/bulk-update`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ contentIds, updates })
      });

      const data = await response.json();

      if (data.success) {
        toast.success(`✅ ${data.data.updated} içerik güncellendi`);
        setShowBulkEditModal(false);
        setSelectionMode(false);
        setSelectedContents([]);
        if (onRefreshContent) await onRefreshContent();
      } else {
        toast.error('❌ Toplu güncelleme hatası: ' + (data.message || data.error || 'Bilinmeyen hata'));
      }
    } catch (error) {
      console.error('Bulk update hatası:', error);
      toast.error('❌ Bağlantı hatası oluştu');
    }
  };

  const handleUpdateQuickButton = async (buttonData) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/quick-buttons/update-all`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(buttonData)
      });

      if (!response.ok) {
        throw new Error('Hızlı butonlar güncellenirken hata oluştu');
      }

      toast.success('Hızlı butonlar başarıyla güncellendi');
      setShowQuickButtonModal(false);

      if (onSearch) {
        onSearch('');
      }
    } catch (error) {
      console.error('Hızlı buton güncelleme hatası:', error);
      toast.error('Hızlı butonlar güncellenirken bir hata oluştu');
    }
  };

  return (
    <div className="library-container">
      {/* Header */}
      <div className="library-header">
        <div className="youtube-search-container" ref={searchContainerRef}>
          <div className="youtube-search-wrapper">
            <input
              type="text"
              className="youtube-search-input"
              placeholder="Başlık veya Etiket ara..."
              value={searchTag}
              onChange={(e) => handleSearch(e.target.value)}
              onFocus={() => setShowSearchDropdown(true)}
            />
            {searchTag && (
              <>
                <button
                  className="search-clear-btn"
                  onClick={handleClearSearch}
                  title="Aramayı temizle"
                >
                  ✕
                </button>
                {selectedTagForLibrary && (
                  <div className="filter-info" style={{
                    position: 'absolute',
                    top: '-25px',
                    left: '0',
                    fontSize: '12px',
                    color: '#667eea',
                    background: '#f7fafc',
                    padding: '2px 8px',
                    borderRadius: '4px',
                    border: '1px solid #e2e8f0'
                  }}>
                    🏷️ Etiket filtresi aktif
                  </div>
                )}
              </>
            )}
          </div>

          {/* Popüler Etiketler Dropdown */}
          {showSearchDropdown && topUsedTags.length > 0 && !searchTag && (
            <div className="search-tags-dropdown">
              <div className="search-tags-header">
                <span className="search-tags-icon">🔥</span>
                <span className="search-tags-title">Popüler Etiketler</span>
              </div>
              <div className="search-tags-list">
                {topUsedTags.map((tag, index) => (
                  <button
                    key={tag.id}
                    className="search-tag-item"
                    onClick={() => handleTagSelect(tag.name)}
                    style={{
                      '--tag-color': tag.color
                    }}
                  >
                    <span className="search-tag-rank">#{index + 1}</span>
                    <span
                      className="search-tag-color"
                      style={{ backgroundColor: tag.color }}
                    />
                    <span className="search-tag-name">{tag.name}</span>
                    <span className="search-tag-count">{tag.usageCount} içerik</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>


        <div className="header-actions">
          {/* Çoklu Seçim Modu Butonu */}
          <button
            onClick={toggleSelectionMode}
            className={`btn-secondary ${selectionMode ? 'btn-active' : ''}`}
            style={{
              background: selectionMode ? 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' : undefined,
              color: selectionMode ? 'white' : undefined,
              border: selectionMode ? 'none' : undefined,
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <span>{selectionMode ? '✓' : '☑️'}</span>
            {selectionMode ? 'Seçim Modundan Çık' : 'Çoklu Seçim'}
          </button>

          {/* Seçim modundayken göster */}
          {selectionMode && selectedContents.length > 0 && (
            <>
              <button
                onClick={handleBulkEdit}
                className="btn-secondary"
                style={{
                  background: 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)',
                  color: 'white',
                  border: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <span>📝</span>
                Çoklu Düzenle ({selectedContents.length})
              </button>

              <button
                onClick={() => setShowBulkWatermarkModal(true)}
                className="btn-secondary"
                style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  border: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
                disabled={selectedVideoCount === 0}
                title={selectedVideoCount === 0 ? 'Seçili video yok' : `${selectedVideoCount} videoya logo ekle`}
              >
                <span>🎬</span>
                Toplu Logo Ekle ({selectedVideoCount})
              </button>

              <button
                onClick={clearSelection}
                className="btn-secondary"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <span>✕</span>
                Seçimi Temizle ({selectedContents.length})
              </button>
            </>
          )}

          {selectionMode && (
            <>
              <button
                onClick={selectAllVideos}
                className="btn-secondary"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <span>📹</span>
                Tüm Videoları Seç
              </button>

              <button
                onClick={handleBulkDelete}
                className="btn-secondary"
                disabled={selectedContents.length === 0}
                style={{
                  background: selectedContents.length > 0
                    ? 'linear-gradient(135deg, #e53e3e 0%, #c53030 100%)'
                    : undefined,
                  color: selectedContents.length > 0 ? 'white' : undefined,
                  border: selectedContents.length > 0 ? 'none' : undefined,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  opacity: selectedContents.length === 0 ? 0.5 : 1
                }}
              >
                <span>🗑️</span>
                Toplu Sil {selectedContents.length > 0 ? `(${selectedContents.length})` : ''}
              </button>
            </>
          )}

          <button
            className="btn-secondary"
            disabled={true}
            title="Bu özellik şu anda bakımdadır"
            style={{
              background: '#a0aec0',
              color: 'white',
              border: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              opacity: 0.6,
              cursor: 'not-allowed'
            }}
          >
            <span>📸</span>
            Instagram'dan Al (Bakımda)
          </button>

          <button
            onClick={() => setShowInstagramProfileModal(true)}
            className="btn-secondary"
            style={{
              background: 'linear-gradient(135deg, #E1306C 0%, #F77737 100%)',
              color: 'white',
              border: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <span>📸</span>
            Instagram Profil Çek
          </button>

          <button
            onClick={() => setShowQuickContentModal(true)}
            className="btn-secondary"
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              border: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <span>⚡</span>
            Hızlı İçerik Ekle
          </button>

          <button
            onClick={() => onAddContent()}
            className="btn-secondary"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <span>➕</span>
            Yeni İçerik Ekle
          </button>

          <button
            onClick={() => setShowQuickButtonModal(true)}
            className="btn-secondary"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <span>🔄</span>
            Hızlı Buton Güncelle
          </button>

          <button
            onClick={handleUpdateStatistics}
            className={`btn-secondary ${isUpdatingStats ? 'btn-loading' : ''}`}
            disabled={isUpdatingStats}
            title="Tüm içeriklerin Telegram istatistiklerini günceller"
          >
            {isUpdatingStats ? (
              <>🔄 Güncelleniyor...</>
            ) : (
              <>İstatistikleri Güncelle</>
            )}
          </button>
        </div>
      </div>

      {/* İçerik İstatistikleri ve Sıralama */}
      <div className="content-stats">
        <div className="stats-left">
          <span className="stats-item">
            📊 Toplam: <strong>{total}</strong> içerik
          </span>
          <span className="stats-item">
            📄 Sayfa: <strong>{currentPage}</strong> / {totalPages || 1}
          </span>
          <span className="stats-item">
            🔍 Gösterilen: <strong>{total > 0 ? `${startIndex + 1}-${endIndex}` : '0'}</strong>
          </span>
        </div>
        <div className="sort-dropdown-container">
          <label htmlFor="sort-select" className="sort-label">Sırala:</label>
          <select
            id="sort-select"
            className="sort-dropdown"
            value={sortBy}
            onChange={(e) => handleSortChange(e.target.value)}
          >
            <optgroup label="Tüm İçerikler">
              <option value="created_at_desc">📋 Tüm İçerikler</option>
            </optgroup>
            <optgroup label="Buton Filtresi">
              <option value="buttons_with">🔘 Butonlu Gönderiler</option>
              <option value="buttons_without">⭕ Butonsuz Gönderiler</option>
            </optgroup>
            <optgroup label="Paylaşım Sayısı">
              <option value="usage_count_desc">📊 En çok paylaşılan</option>
              <option value="usage_count_asc">📊 En az paylaşılan</option>
            </optgroup>
            <optgroup label="Görüntülenme">
              <option value="views_desc">👁️ En çok görüntülenen</option>
              <option value="views_asc">👁️ En az görüntülenen</option>
            </optgroup>
            <optgroup label="Tarih">
              <option value="created_at_asc">📅 En eski eklenen</option>
              <option value="updated_at_desc">🔄 Son güncellenen</option>
              <option value="updated_at_asc">🔄 İlk güncellenen</option>
            </optgroup>
            <optgroup label="Alfabetik">
              <option value="title_asc">🔤 A'dan Z'ye</option>
              <option value="title_desc">🔤 Z'den A'ya</option>
            </optgroup>
          </select>
        </div>
      </div>

      {/* İçerik Listesi */}
      <div className="content-list">
        {filteredContent.length === 0 ? (
          searchTag ? (
            <div className="no-results">
              <h4>🔍 Sonuç Bulunamadı</h4>
              <p>"{searchTag}" araması için sonuç bulunamadı.</p>
              <button
                onClick={() => {
                  setSearchTag('');
                  handleSearch('');
                }}
                className="btn-clear-search"
              >
                Aramayı Temizle
              </button>
            </div>
          ) : sortBy === 'buttons_without' ? (
            <div className="no-results">
              <h4>⭕ Butonsuz Gönderi Bulunamadı</h4>
              <p>Kütüphanede butonsuz gönderi bulunmamaktadır.</p>
              <p style={{ marginTop: '10px', fontSize: '14px', color: '#718096' }}>
                Yukarıdaki dropdown menüden farklı bir filtre seçebilirsiniz.
              </p>
            </div>
          ) : sortBy === 'buttons_with' ? (
            <div className="no-results">
              <h4>🔘 Butonlu Gönderi Bulunamadı</h4>
              <p>Kütüphanede butonlu gönderi bulunmamaktadır.</p>
              <p style={{ marginTop: '10px', fontSize: '14px', color: '#718096' }}>
                Yukarıdaki dropdown menüden farklı bir filtre seçebilirsiniz.
              </p>
            </div>
          ) : (
            <div className="no-content">
              <p>Henüz içerik eklenmemiş.</p>
            </div>
          )
        ) : (
          <>
            <Masonry
              breakpointCols={breakpointColumns}
              className="masonry-grid"
              columnClassName="masonry-grid-column"
            >
              {currentContent.map(content => {
                const isExpanded = expandedCards[content.id] || false;
                const fullText = content.content || '';
                const lines = fullText.split('\n');
                const maxLines = 2;
                const shouldTruncate = lines.length > maxLines;

                let displayText = fullText;
                if (!isExpanded && shouldTruncate) {
                  displayText = lines.slice(0, maxLines).join('\n');
                }

                const mediaItems = parseMediaData(content);
                const isUpdating = updatingContentIds.has(content.id);
                const isEditingTags = editingTagsForContent === content.id;

                // Bu içeriğe eklenebilecek etiketleri bul
                const availableTagsForContent = tags.filter(tag =>
                  !content.content_tags?.some(ct => ct.id === tag.id)
                );

                return (
                  <div
                    key={content.id}
                    className={`content-card ${selectionMode && selectedContents.includes(content.id) ? 'selected' : ''} ${showAddTagDropdown === content.id || creatingTagForContent === content.id ? 'tag-dropdown-open' : ''}`}
                    onClick={() => selectionMode && toggleContentSelection(content.id)}
                    style={{ cursor: selectionMode ? 'pointer' : 'default' }}
                  >
                    {/* Seçim Checkbox'ı */}
                    {selectionMode && (
                      <div className="selection-checkbox">
                        <input
                          type="checkbox"
                          checked={selectedContents.includes(content.id)}
                          onChange={() => toggleContentSelection(content.id)}
                          onClick={(e) => e.stopPropagation()}
                        />
                        {(() => {
                          const mediaItems = parseMediaData(content);
                          const hasVideo = mediaItems.some(item => item.type === 'video');
                          return hasVideo ? <span className="video-indicator">📹</span> : null;
                        })()}
                      </div>
                    )}
                    <div className="card-body">
                      {/* 1. Başlık ve Kullanım Sayısı */}
                      <div className="content-header">
                        <h4>{content.title}</h4>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          {content.total_stats && (content.total_stats.views > 0 || content.total_stats.total > 0) && (
                            <div className="quick-stats-badge">
                              <span title="Görüntülenme">
                                👁️ {content.total_stats.views >= 1000000
                                  ? (content.total_stats.views / 1000000).toFixed(1) + 'M'
                                  : content.total_stats.views >= 1000
                                    ? (content.total_stats.views / 1000).toFixed(1) + 'K'
                                    : content.total_stats.views}
                              </span>
                              {content.total_stats.reactions > 0 && (
                                <span title="Beğeni">
                                  ❤️ {content.total_stats.reactions >= 1000
                                    ? (content.total_stats.reactions / 1000).toFixed(1) + 'K'
                                    : content.total_stats.reactions}
                                </span>
                              )}
                              {content.total_stats.forwards > 0 && (
                                <span title="İletim">
                                  ↗️ {content.total_stats.forwards >= 1000
                                    ? (content.total_stats.forwards / 1000).toFixed(1) + 'K'
                                    : content.total_stats.forwards}
                                </span>
                              )}
                            </div>
                          )}

                          <span className="usage-badge" onClick={() => onShowHistory(content)}>
                            📊 {content.usage_count || content.total_stats?.total || 0}
                          </span>

                          <button
                            onClick={() => onEditContent(content)}
                            className="icon-btn edit-icon"
                            title="Düzenle"
                          >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                            </svg>
                          </button>
                        </div>
                      </div>

                      {/* 2. Medya Önizleme */}
                      {mediaItems.length > 0 && (
                        <div className="content-media-preview">
                          {mediaItems.length > 1 ? (
                            <div className="media-carousel">
                              <div className="media-count-badge">
                                📁 {mediaItems.length} Medya
                              </div>
                              <div className="media-grid-container">
                                {mediaItems.slice(0, 4).map((item, index) => (
                                  <div key={index} className="media-grid-item">
                                    {item.type === 'video' ? (
                                      <div className="video-thumbnail-wrapper">
                                        <video
                                          className="video-thumbnail"
                                          muted
                                          preload="metadata"
                                          onLoadedMetadata={(e) => {
                                            e.target.currentTime = 1;
                                          }}
                                        >
                                          <source src={item.url} type="video/mp4" />
                                        </video>
                                        <div className="media-overlay">
                                          <span className="play-icon">▶️</span>
                                        </div>
                                      </div>
                                    ) : (
                                      <div className="image-thumbnail-wrapper">
                                        <img
                                          src={item.url}
                                          alt={`Media ${index + 1}`}
                                          className="image-thumbnail"
                                          loading="lazy"
                                          onError={(e) => {
                                            e.target.parentElement.innerHTML = `
                                              <div class="media-error-placeholder">
                                                <span>🖼️</span>
                                                <small>Yüklenemedi</small>
                                              </div>
                                            `;
                                          }}
                                        />
                                      </div>
                                    )}
                                  </div>
                                ))}
                                {mediaItems.length > 4 && (
                                  <div className="more-media-indicator">
                                    <span>+{mediaItems.length - 4}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          ) : (
                            <div className="single-media-container">
                              {mediaItems[0].type === 'image' ? (
                                <div className="image-wrapper">
                                  <img
                                    src={mediaItems[0].url}
                                    alt={content.title}
                                    className="content-image"
                                    loading="lazy"
                                    onError={(e) => {
                                      e.target.parentElement.innerHTML = `
                                        <div class="media-error-placeholder">
                                          <span class="error-icon">🖼️</span>
                                          <span class="error-text">Resim yüklenemedi</span>
                                        </div>
                                      `;
                                    }}
                                  />
                                </div>
                              ) : (
                                <div className="video-wrapper">
                                  <video
                                    controls
                                    className="content-video"
                                    preload="metadata"
                                    controlsList="nodownload"
                                    style={{
                                      width: '100%',
                                      height: '100%',
                                      objectFit: 'cover'
                                    }}
                                  >
                                    <source src={mediaItems[0].url} type="video/mp4" />
                                    <div className="media-error-placeholder">
                                      <span className="error-icon">📹</span>
                                      <span className="error-text">Video oynatılamıyor</span>
                                    </div>
                                  </video>
                                </div>
                              )}
                              <div className="media-type-badge">
                                {mediaItems[0].type === 'video' ? '📹 Video' : '🖼️ Resim'}
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* 3. İçerik Açıklaması */}
                      <div className="content-preview-wrapper">
                        <pre
                          className={`content-preview ${isExpanded ? 'expanded' : ''}`}
                          onClick={() => shouldTruncate && toggleExpand(content.id)}
                          style={{
                            cursor: shouldTruncate ? 'pointer' : 'default'
                          }}
                          dangerouslySetInnerHTML={{
                            __html: (() => {
                              let text = displayText || '';
                              text = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
                              text = text.replace(
                                /\[([^\]]+)\]\(([^)]+)\)/g,
                                '<a class="content-inline-link" href="$2" target="_blank" rel="noopener noreferrer" title="$2" onclick="event.stopPropagation()">$1</a>'
                              );
                              if (!isExpanded && shouldTruncate) {
                                text += '<span class="show-more-text">Devamını Göster...</span>';
                              }
                              return text;
                            })()
                          }}
                        />
                      </div>

                      {/* 4. Telegram Butonları */}
                      {content.buttons && content.buttons !== '[]' && content.buttons !== 'null' && (
                        <div className="telegram-buttons-container">
                          {(() => {
                            try {
                              const buttons = typeof content.buttons === 'string'
                                ? JSON.parse(content.buttons)
                                : content.buttons;
                              if (!Array.isArray(buttons) || buttons.length === 0) return null;

                              return (
                                <div className="telegram-buttons">
                                  {buttons.map((row, rowIndex) => (
                                    <div key={rowIndex} className="telegram-button-row">
                                      {(Array.isArray(row) ? row : [row]).map((button, btnIndex) => (
                                        button && button.text && (
                                          <a
                                            key={btnIndex}
                                            href={button.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="telegram-button"
                                          >
                                            <span>{button.text}</span>
                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                              <path d="M7 17L17 7M17 7H7M17 7V17" />
                                            </svg>
                                          </a>
                                        )
                                      ))}
                                    </div>
                                  ))}
                                </div>
                              );
                            } catch (e) {
                              console.error('Button parse error:', e);
                              return (
                                <div className="button-error">
                                  ⚠️ Buton formatı hatalı
                                </div>
                              );
                            }
                          })()}
                        </div>
                      )}

                      {/* 5. Etiketler - YENİ TASARIM */}
                      <div className="content-tags-wrapper">
                        <div className={`content-tags ${isEditingTags ? 'editing-mode' : ''}`}>
                          {/* Yeni Etiket Oluştur Butonu - En Başta */}
                          {isEditingTags && creatingTagForContent !== content.id && (
                            <button
                              className="create-tag-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                setCreatingTagForContent(content.id);
                                setShowAddTagDropdown(null);
                              }}
                              title="Yeni etiket oluştur"
                            >
                              ➕
                            </button>
                          )}

                          {/* Yeni Etiket Input'u */}
                          {isEditingTags && creatingTagForContent === content.id && (
                            <div className="new-tag-input-wrapper">
                              <input
                                type="text"
                                className="new-tag-input"
                                placeholder="Etiket adı..."
                                value={newTagName}
                                onChange={(e) => setNewTagName(e.target.value)}
                                onKeyDown={(e) => handleNewTagKeyDown(e, content.id)}
                                autoFocus
                                disabled={isCreatingTag}
                              />
                              {isCreatingTag && <span className="tag-loading">⏳</span>}
                            </div>
                          )}

                          {content.content_tags && content.content_tags.map(tag => (
                            <span
                              key={tag.id}
                              className={`content-tag-badge ${isEditingTags ? 'shaking' : ''}`}
                              style={{
                                backgroundColor: tag.color,
                                color: '#fff',
                                cursor: isEditingTags ? 'default' : 'pointer'
                              }}
                              onClick={(e) => {
                                if (!isEditingTags) {
                                  e.stopPropagation();
                                  handleTagSelect(tag.name);
                                }
                              }}
                              title={isEditingTags ? '' : `"${tag.name}" etiketine göre filtrele`}
                            >
                              <span className="tag-icon">🏷️</span>
                              {tag.name}
                              {isEditingTags && (
                                <button
                                  className="tag-remove-btn"
                                  onClick={(e) => handleRemoveTag(content.id, tag.id, e)}
                                  title="Etiketi kaldır"
                                >
                                  ✕
                                </button>
                              )}
                            </span>
                          ))}

                          {/* Mevcut Etiket Ekle Butonu */}
                          {isEditingTags && (
                            <button
                              className="add-tag-btn"
                              onClick={(e) => toggleAddTagDropdown(content.id, e)}
                              title={showAddTagDropdown === content.id ? "Listeyi kapat" : "Mevcut etiketlerden ekle"}
                            >
                              {showAddTagDropdown === content.id ? '✕' : '📋'}
                            </button>
                          )}
                        </div>

                        {/* Düzenle Butonu */}
                        <button
                          className={`tag-edit-btn ${isEditingTags ? 'active' : ''}`}
                          onClick={(e) => toggleTagEditMode(content.id, e)}
                          title={isEditingTags ? "Düzenlemeyi bitir" : "Etiketleri düzenle"}
                        >
                          {isEditingTags ? '✓' : '✏️'}
                        </button>
                      </div>

                      {isEditingTags && showAddTagDropdown === content.id && (
                        <div className="add-tag-inline-list">
                          {availableTagsForContent.length > 0 ? (
                            <>
                              <div className="inline-list-header">
                                <span>📋 Mevcut Etiketler</span>
                                <span className="tag-count">{availableTagsForContent.length} etiket</span>
                              </div>
                              <div className="inline-tag-items">
                                {availableTagsForContent.map(tag => (
                                  <button
                                    key={tag.id}
                                    className="inline-tag-item"
                                    onClick={(e) => handleAddTagToContent(content.id, tag.id, e)}
                                    style={{
                                      backgroundColor: tag.color,
                                      color: '#fff'
                                    }}
                                  >
                                    <span className="tag-icon">🏷️</span>
                                    {tag.name}
                                    <span className="add-icon">+</span>
                                  </button>
                                ))}
                              </div>
                            </>
                          ) : (
                            <div className="inline-list-empty">
                              Tüm etiketler eklendi! 🎉
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Footer */}
                    <div className="card-footer-info">
                      <div className="footer-left">
                        <span className="footer-username">
                          {content.updated_by_username || content.created_by_username || content.created_by || content.username || 'Bilinmiyor'}
                        </span>
                        <span className="footer-date">
                          {formatDate(content.updated_at || content.created_at)}
                        </span>
                      </div>

                      <div className="footer-actions">
                        <div className="stats-update-section">
                          <span className="stats-update-time">
                            Son Güncelleme: {formatStatsUpdateTime(content)}
                          </span>
                          <button
                            onClick={(e) => handleUpdateContentStats(content.id, e)}
                            disabled={isUpdating}
                            className={`stats-refresh-btn ${isUpdating ? 'updating' : ''}`}
                            title="Bu içeriğin istatistiklerini güncelle"
                          >
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </Masonry>

            {/* Sayfalandırma Kontrolleri */}
            {totalPages > 1 && (
              <div className="pagination-container">
                <button
                  className="pagination-btn pagination-prev"
                  onClick={goToPreviousPage}
                  disabled={currentPage === 1}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="15 18 9 12 15 6"></polyline>
                  </svg>
                  Önceki
                </button>

                <div className="pagination-numbers">
                  {getPageNumbers().map((number, index) => (
                    number === '...' ? (
                      <span key={`dots-${index}`} className="pagination-dots">...</span>
                    ) : (
                      <button
                        key={number}
                        className={`pagination-number ${currentPage === number ? 'active' : ''}`}
                        onClick={() => goToPage(number)}
                      >
                        {number}
                      </button>
                    )
                  ))}
                </div>

                <button
                  className="pagination-btn pagination-next"
                  onClick={goToNextPage}
                  disabled={currentPage === totalPages}
                >
                  Sonraki
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="9 18 15 12 9 6"></polyline>
                  </svg>
                </button>
              </div>
            )}
          </>
        )}
      </div>

      <QuickContentModal
        isOpen={showQuickContentModal}
        onClose={() => setShowQuickContentModal(false)}
        onSubmit={handleQuickContentSubmit}
        tags={tags}
        API_URL={API_URL}
      />

      <QuickButtonUpdateModal
        isOpen={showQuickButtonModal}
        onClose={() => setShowQuickButtonModal(false)}
        onSuccess={handleUpdateQuickButton}
        API_URL={API_URL}
      />

      <InstagramImportModal
        isOpen={showInstagramImportModal}
        onClose={() => setShowInstagramImportModal(false)}
        API_URL={API_URL}
        tags={tags}
        onSuccess={() => {
          setShowInstagramImportModal(false);
          if (onRefreshContent) onRefreshContent();
        }}
      />

      <BulkVideoWatermarkModal
        isOpen={showBulkWatermarkModal}
        onClose={() => setShowBulkWatermarkModal(false)}
        selectedContents={getSelectedVideoContents()}
        API_URL={API_URL}
        onSuccess={() => {
          setShowBulkWatermarkModal(false);
          setSelectionMode(false);
          setSelectedContents([]);
          if (onRefreshContent) onRefreshContent();
          toast.success('Toplu logo ekleme tamamlandı!');
        }}
      />

      <InstagramProfileModal
        isOpen={showInstagramProfileModal}
        onClose={() => setShowInstagramProfileModal(false)}
        tags={tags}
        onSuccess={() => {
          setShowInstagramProfileModal(false);
          if (onRefreshContent) onRefreshContent();
        }}
      />

      <ContentModal
        isOpen={showBulkEditModal}
        onClose={() => setShowBulkEditModal(false)}
        contentForm={bulkEditForm}
        setContentForm={setBulkEditForm}
        onSubmit={() => { }}
        editingContentId={null}
        buttonForm={bulkButtonForm}
        setButtonForm={setBulkButtonForm}
        tags={tags}
        bulkMode={true}
        bulkContentIds={selectedContents}
        onBulkSubmit={handleBulkSubmit}
      />
    </div>
  );
};

export default LibraryTab;